import type { FetchSignerResult, Signer } from "@wagmi/core"
import { type BigNumber } from "ethers"
import { useSwitchNetwork } from "wagmi"
import { useEffect, useState } from "react"

import { getCorrectChain, type UseCorrectChainErrors } from "web3/helpers/chain"
import { useMe } from "user/providers/MeProvider"
import { getTokenWrapper } from "web3/hooks/useTokenWrapper"
import { useToast } from "common/hooks/useToast"
import { useTransaction } from "web3/providers/TransactionProvider"
import type {
  TransactionOnMining,
  TransactionOnSuccess,
  TransactionStatus,
} from "web3/types/transaction"
import { TransactionState, TransactionToastState } from "web3/types/transaction"
import { getChainIdParams } from "web3/helpers/chainId"
import { getChainReferenceName } from "web3/helpers/chainReference"
import { useSafeTransactionV2 } from "safe/hooks/useSafeTransactionV2"
import { useTokenWrapperAttemptMutation } from "token/hooks/useTokenWrapperActionAttempt"
import { getAccountId } from "web3/helpers/accountId"
import { useAddNetwork } from "web3/hooks/useAddNetwork"
import { useAddress } from "web3/hooks/useAddress"
import { isSameAddress } from "web3/helpers/address"
import { useMultichainAccount } from "web3/hooks/useMultichainAccount"

enum TokenWrapperError {
  Signernotfound = "SIGNER_NOT_FOUND",
  Nonmatchingchain = "NON_MATCHING_CHAIN",
}

type Error = UseCorrectChainErrors | TokenWrapperError

type WrapArguments = {
  signer?: FetchSignerResult<Signer>
  address: string
  amount: BigNumber
  tokenContractAddress: string
  chainId: string
  optimisticBalance?: number
}

type Metadata = {
  address: string
  amount: BigNumber
  chainId: string
  tokenContractAddress: string
  actionType: "wrap" | "unwrap" | "approve"
  optimisticBalance?: number
}

export const useTokenWrapperTransaction = () => {
  const [error, setError] = useState<Error | undefined>(undefined)
  const { toast } = useToast()
  const address = useAddress()
  const { switchNetwork } = useSwitchNetwork()
  const { addNetwork } = useAddNetwork()
  const [safeTxHash, setSafeTxHash] = useState<string | undefined>(undefined)

  const { mutate: saveTokenWrapperAttempt } = useTokenWrapperAttemptMutation()

  const me = useMe()
  const { multichainAccount: walletAccount } = useMultichainAccount()
  const canUseSafeTx =
    me?.type === "SAFE" && !isSameAddress(me.address, walletAccount.address)

  const [transactionState, setTransactionState] = useState<
    TransactionStatus<Metadata>
  >({
    state: TransactionState.None,
  })

  const {
    proposeMultiGnosisTransaction,
    buildSafeTransaction,
    onPendingSafeTx,
  } = useSafeTransactionV2(
    {
      safeId: me?.id as string,
      accountAddress: me?.address,
    },
    { enabled: canUseSafeTx },
  )

  const onMining: TransactionOnMining<Metadata> = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    transaction,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    metadata,
  }) => {
    if (address) {
      if (metadata.actionType === "wrap" || metadata.actionType === "unwrap") {
        const tokenId = getAccountId({
          address: metadata.tokenContractAddress,
          chainId: metadata.chainId,
        })
        saveTokenWrapperAttempt({
          txHash: transaction.hash,
          address: metadata.address,
          tokenId: tokenId,
          actionType: metadata.actionType,
          amount: metadata.amount,
          optimisticBalance: metadata.optimisticBalance ?? undefined,
        })

        return
      }
    }
  }

  const onSuccess: TransactionOnSuccess<Metadata> = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    transaction,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    metadata,
  }) => {
    // TODO(@nicolas): Pending
  }

  const options = {
    on: {
      [TransactionToastState.Mining]: onMining,
      [TransactionToastState.Success]: onSuccess,
    },
  }

  const { send, status, setMetadata } = useTransaction<Metadata>(options)

  useEffect(() => {
    if (!status) return

    setTransactionState(status)
  }, [status])

  // Wrap
  const depositFor = async ({
    optimisticBalance,
    signer,
    address,
    amount,
    tokenContractAddress,
    chainId,
  }: WrapArguments): Promise<void> => {
    if (!signer) {
      setError(TokenWrapperError.Signernotfound)
      toast({
        title: "No signer found",
        status: "error",
        description: "You should have your wallet connected",
      })

      return
    }

    const chainRef = await signer.getChainId()

    const { isCorrectChain } = getCorrectChain({
      chainId,
      chainReference: chainRef,
    })

    if (!isCorrectChain) {
      const { reference } = getChainIdParams(chainId)

      const chainReferenceName = getChainReferenceName(reference)

      setError(TokenWrapperError.Nonmatchingchain)

      toast({
        status: "warning",
        title: "Incorrect network",
        description: `You must be connected to the ${chainReferenceName} network to wrap these tokens`,
        duration: 5000,
      })

      await switchNetwork?.(reference)

      addNetwork(chainId)

      return
    }

    const tokenWrapper = getTokenWrapper(tokenContractAddress, signer)

    setMetadata({
      address,
      amount,
      chainId,
      tokenContractAddress,
      actionType: "wrap",
      optimisticBalance,
    })

    if (canUseSafeTx) {
      try {
        const safeTxHash = await proposeMultiGnosisTransaction([
          buildSafeTransaction(
            tokenWrapper,
            "depositFor",
            [address, amount],
            tokenContractAddress,
          ),
        ])

        if (safeTxHash) {
          setTransactionState({
            state: TransactionState.PendingSafe,
            metadata: {
              address,
              amount,
              chainId,
              tokenContractAddress,
              actionType: "wrap",
              optimisticBalance,
            },
            safeTxHash: safeTxHash,
          })

          setTimeout(() => {
            setTransactionState({
              state: TransactionState.None,
            })
          }, 2000)
        }

        onPendingSafeTx(safeTxHash, "", {
          requiredParameters: ["account", "value"],
          expectedMethod: "depositFor",
        })((values) =>
          handlePendingSafeTx({ ...values, safeTxHash: values.safeTxHash }),
        )
      } catch (error: any) {
        if (error && error.message.includes("user rejected signing")) {
          toast({
            status: "error",
            title: "User rejected signature",
            duration: 5000,
          })
        }
      }

      return
    } else {
      send(() => tokenWrapper.depositFor(address, amount))
    }
  }

  // Unwrap
  const withdrawTo = async ({
    optimisticBalance,
    signer,
    address,
    amount,
    tokenContractAddress,
    chainId,
  }: WrapArguments) => {
    if (!signer) {
      setError(TokenWrapperError.Signernotfound)
      toast({
        title: "No signer found",
        status: "error",
        description: "You should have your wallet connected",
      })

      return
    }

    const chainRef = await signer.getChainId()

    const { isCorrectChain } = getCorrectChain({
      chainId,
      chainReference: chainRef,
    })

    if (!isCorrectChain) {
      const { reference } = getChainIdParams(chainId)

      const chainReferenceName = getChainReferenceName(reference)

      setError(TokenWrapperError.Nonmatchingchain)

      toast({
        status: "warning",
        title: "Incorrect network",
        description: `You must be connected to the ${chainReferenceName} network to unwrap these tokens`,
        duration: 5000,
      })

      await switchNetwork?.(reference)

      addNetwork(chainId)

      return
    }

    const tokenWrapper = getTokenWrapper(tokenContractAddress, signer)

    setMetadata({
      address,
      amount,
      tokenContractAddress,
      chainId,
      actionType: "unwrap",
      optimisticBalance,
    })

    if (canUseSafeTx) {
      try {
        const safeTxHash = await proposeMultiGnosisTransaction([
          buildSafeTransaction(
            tokenWrapper,
            "withdrawTo",
            [address, amount],
            tokenContractAddress,
          ),
        ])

        if (safeTxHash) {
          setTransactionState({
            state: TransactionState.PendingSafe,
            metadata: {
              address,
              chainId,
              amount,
              tokenContractAddress,
              actionType: "unwrap",
              optimisticBalance,
            },
            safeTxHash: safeTxHash,
          })

          setTimeout(() => {
            setTransactionState({
              state: TransactionState.None,
            })
          }, 2000)
        }

        onPendingSafeTx(safeTxHash, "", {
          requiredParameters: ["account", "value"],
          expectedMethod: "withdrawTo",
        })((values) =>
          handlePendingSafeTx({ ...values, safeTxHash: values.safeTxHash }),
        )
      } catch (error: any) {
        if (error && error.message.includes("user rejected signing")) {
          toast({
            status: "error",
            title: "User rejected signature",
            duration: 5000,
          })
        }
      }

      return
    } else {
      send(() => tokenWrapper.withdrawTo(address, amount))
    }
  }

  const handlePendingSafeTx = (values: Record<string, any>) => {
    if (values.safeTxHash) {
      setSafeTxHash(values.safeTxHash)
    }
  }

  // Approve governance token to spend underlying tokens
  const approve = async ({
    signer,
    address: spender,
    amount,
    tokenContractAddress,
    chainId,
  }: WrapArguments) => {
    if (!signer) {
      setError(TokenWrapperError.Signernotfound)
      toast({
        title: "No signer found",
        status: "error",
        description: "You should have your wallet connected",
      })

      return
    }

    const chainRef = await signer.getChainId()

    const { isCorrectChain } = getCorrectChain({
      chainId,
      chainReference: chainRef,
    })

    if (!isCorrectChain) {
      const { reference } = getChainIdParams(chainId)

      const chainReferenceName = getChainReferenceName(reference)

      setError(TokenWrapperError.Nonmatchingchain)

      toast({
        status: "warning",
        title: "Incorrect network",
        description: `You must be connected to the ${chainReferenceName} network to approve`,
        duration: 5000,
      })

      await switchNetwork?.(reference)

      addNetwork(chainId)

      return
    }

    const tokenWrapper = getTokenWrapper(tokenContractAddress, signer)

    setMetadata({
      address: spender,
      chainId,
      amount,
      tokenContractAddress,
      actionType: "approve",
    })

    if (canUseSafeTx) {
      try {
        const safeTxHash = await proposeMultiGnosisTransaction([
          buildSafeTransaction(
            tokenWrapper,
            "approve",
            [spender, amount],
            tokenContractAddress,
          ),
        ])

        if (safeTxHash) {
          setTransactionState({
            state: TransactionState.PendingSafe,
            metadata: {
              address: spender,
              chainId,
              amount,
              tokenContractAddress,
              actionType: "approve",
            },
            safeTxHash: safeTxHash,
          })

          setTimeout(() => {
            setTransactionState({
              state: TransactionState.None,
            })
          }, 2000)
        }

        onPendingSafeTx(safeTxHash, "", {
          requiredParameters: ["spender", "value"],
          expectedMethod: "approve",
        })((values) =>
          handlePendingSafeTx({ ...values, safeTxHash: values.safeTxHash }),
        )
      } catch (error: any) {
        if (error && error.message.includes("user rejected signing")) {
          toast({
            status: "error",
            title: "User rejected signature",
            duration: 5000,
          })
        }
      }

      return
    } else {
      send(() => tokenWrapper.approve(spender, amount))
    }
  }

  return {
    depositFor,
    withdrawTo,
    approve,
    error,
    status: transactionState,
    safeTxHash,
  }
}
